import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import type { CurrentStakeType } from '../../../../types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { tFunction, useTranslation } from 'app/lang';
import { STAKE_TYPE_DICTIONARY, StakeType, StakeTypeName } from 'app/lib/staking/constants';
import Dropdown, { ChangeHandler, Control, DropdownSize, Option } from 'app/widgets/Dropdown/Single';

import { UpdateCheckedStakeType, updateCheckedStakeType } from '../../../../actions/stakeTypes';
import { selectAvailableStakeTypes, selectCurrentStakeType } from '../../../../selectors/stakeTypes';
import { selectConflictState } from '../../../../selectors/state';
import type { AvailableStakeType } from '../../../../types/stakeTypes';

import dictionary from './StakeTypeSelect.dictionary.json';

import styles from './StakeTypeSelector.module.scss';

// ---

type StakeTypeOptionProps = { label: string; value: StakeType };

export const getStakeTypeOptions = (t: tFunction): StakeTypeOptionProps[] => ([
  { label: t(STAKE_TYPE_DICTIONARY[StakeType.SINGLE]), value: StakeType.SINGLE },
  { label: t(STAKE_TYPE_DICTIONARY[StakeType.EXPRESS]), value: StakeType.EXPRESS },
  // { label: t(STAKE_TYPE_DICTIONARY[StakeType.SYSTEM]), value: StakeType.SYSTEM },закоментировано в связи с задачей https://jira.tennisi.it/browse/CP-1101
]);

type StoreProps = {
  stakeType: CurrentStakeType;
  available: AvailableStakeType;
  hasConflict: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<UpdateCheckedStakeType>;
};

const StakeTypeSelect: FC<StoreProps & DispatchProp> = ({
  stakeType,
  available,
  hasConflict,
  dispatch,
}) => {
  const { t, lang } = useTranslation();
  const selectOperationModeHandler: ChangeHandler<StakeTypeOptionProps> = useCallback((option) => {
    if (!option || !('value' in option)) {
      console.error('StakeTypeSelect -> selectOperationModeHandler -> `option` not found', option);
      return;
    }

    if (!stakeType.has(option.value)) {
      dispatch(updateCheckedStakeType(option.value));
    }
  }, [stakeType]);
  const stakeTypeOptions = getStakeTypeOptions(t);
  const options = useMemo(() => (
    stakeTypeOptions.map((option) => (
      available.has(option.value)
        ? option
        : { ...option, isDisabled: true }
    ))
  ), [available, lang]);

  const value = options.find((option) => option.value === stakeType.first());

  return (
    <Dropdown<StakeTypeOptionProps>
        dynamicOptions
        size={DropdownSize.SMALL}
        isSearchable={false}
        options={options}
        value={value}
        className={styles.dropdown}
        indicatorSize={20}
        disabled={hasConflict}
        onChange={selectOperationModeHandler}
        components={{
          Control: (props) => (
            <Control {...props}
                     customInnerProps={{
                       title: t(dictionary.controlTitle),
                       ['data-ga-event']: 'cart-bidTypeMenu',
                     }}>
              {props.children}
            </Control>
          ),
          Option: (props) => {
            return (
              <Option {...props}
                      customInnerProps={{
                        ['data-ga-event']: 'cart-bidTypeSelect',
                        ['data-ga-event-value']: StakeTypeName[props.data.value],
                      }}>
                {props.children}
              </Option>
            );
          },
        }} />
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  stakeType: selectCurrentStakeType(state),
  available: selectAvailableStakeTypes(state),
  hasConflict: selectConflictState(state) !== null,
});

export default connect(mapStateToProps)(StakeTypeSelect);
